import { useCallback, useEffect, useRef, useState } from "react";
import { getCrateCxItems } from "@services/api";
import { notifyError } from "@components/ds/notify";
import { USE_FIXTURES } from "../constants";
import { getCrateItemsState } from "../__fixtures__/fixtureStore";

// Owns the GET /get-crate-cx-items round-trip. Caller hands in a crateId and
// active tab; the hook fetches on mount + whenever those change, and exposes
// reload() for the caller to invoke after every write (pack / unpack / mark /
// add-to-bag) — mirrors fetchData() in crate-cx-items.vue.
//
// `crateId` may be empty initially (the user hasn't picked a crate yet); the
// hook stays idle in that case and `data` remains null.

const useCrateData = ({ crateId, tab = "today" }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const isMounted = useRef(true);
  // Monotonic request token — only the latest in-flight request is allowed
  // to set state. Prevents stale fetch races when the user changes tab or
  // crate id while a previous fetch is still resolving.
  const reqId = useRef(0);

  useEffect(
    () => () => {
      isMounted.current = false;
    },
    [],
  );

  const reload = useCallback(async () => {
    if (!crateId) {
      if (isMounted.current) setData(null);
      return;
    }
    const my = ++reqId.current;
    setLoading(true);
    if (USE_FIXTURES) {
      if (isMounted.current && my === reqId.current) {
        // Lookup the per-crate fixture from the mutable store (so
        // pack/unpack/marking writes show up on the next reload). Falls
        // back to null if the crate id isn't in the fixture map.
        const payload = getCrateItemsState(crateId) || null;
        setData(payload);
        setLoading(false);
      }
      return;
    }
    try {
      const res = await getCrateCxItems({ crate_id: crateId, tab });
      const payload = res?.data?.data || res?.data || {};
      if (isMounted.current && my === reqId.current) setData(payload);
    } catch (e) {
      if (my !== reqId.current) return;
      const msg =
        e?.response?.data?.error ||
        e?.response?.data?.message ||
        e?.message ||
        "Failed to fetch crate items";
      notifyError(msg);
      if (isMounted.current) setData(null);
    } finally {
      if (isMounted.current && my === reqId.current) setLoading(false);
    }
  }, [crateId, tab]);

  useEffect(() => {
    reload();
  }, [reload]);

  return { data, loading, reload, setData };
};

export default useCrateData;
